/**
 * useOutreachFilterPreviewCount — the Outreach filter panel's live "Show N
 * creators" count. Same list query the main table runs (`fetchOutreachRows`),
 * at page 1 / limit 1 (only `total` is read), so the count always matches
 * what applying these filters would actually show. `enabled` gates the
 * fetch to while the panel is open, so adjusting filters never fetches in
 * the background once it's closed. `keepPreviousData` holds the last count
 * on screen while a new one is in flight, instead of flashing blank.
 */
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { OutreachRow } from '@discovr/types';
import { campaignKeys } from '../queryKeys';
import { fetchOutreachRows, type OutreachRowsParams } from '../api/outreachApi';

export type OutreachFilterPreviewParams = Pick<
  OutreachRowsParams,
  'tab' | 'q' | 'reached_from' | 'reached_to' | 'stage' | 'intent' | 'locked_rate'
>;

export function useOutreachFilterPreviewCount(campaignId: string, params: OutreachFilterPreviewParams, enabled: boolean) {
  const fullParams: OutreachRowsParams = { ...params, page: 1, limit: 1 };
  return useQuery<{ rows: OutreachRow[]; total: number }>({
    queryKey: campaignKeys.outreachRows(campaignId, fullParams),
    queryFn: () => fetchOutreachRows(campaignId, fullParams),
    enabled: enabled && !!campaignId,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
}
